import { type ClassValue, clsx } from 'clsx'
import { format, isValid, parseISO } from 'date-fns'
import { extendTailwindMerge } from 'tailwind-merge'

const DOMAIN_PROTOCOL_PATTERN = /^https?:\/\//
const DOMAIN_WWW_PATTERN = /^www\./

// Trellis extends Tailwind with custom typography and surface utilities
// (text-title-*, text-subtitle-*, text-body-*, text-bold-*) defined as
// @utility directives in globals.css. Without this extension, tailwind-merge
// treats them as text-color candidates and drops legitimate color classes like
// text-primary-foreground / text-muted-foreground when both appear together —
// the result is invisible button text. Found during /browse dogfood of
// PR #2934 (typography sweep on shared UI primitives).
// Surface depth utilities set box-shadow, so they must conflict with shadow-*.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        { 'text-title': ['2xl', 'xl', 'lg', 'md', 'sm'] },
        { 'text-subtitle': ['xl', 'lg', 'md', 'sm', 'xs', '2xs', '3xs'] },
        { 'text-body': ['sm', 'xs', '2xs', '3xs'] },
        { 'text-bold': ['sm', 'xs', '2xs', 'section'] },
      ],
      shadow: ['surface-bordered', 'surface-lifted', 'surface-elevated', 'surface-accent'],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

interface FormatDateOptions {
  hour12?: boolean
  locale?: string
  yesterdayLabel?: string
}

export function formatDate(date: Date | string, options: FormatDateOptions = {}): string {
  const d = new Date(date)
  const now = new Date()
  const { locale, yesterdayLabel, hour12 } = options

  // Reset time part for calendar day comparison
  const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const nDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  const diffTime = nDate.getTime() - dDate.getTime()
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    // Today: show time
    return d
      .toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: hour12 ?? undefined,
      })
      .replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase())
  }
  if (diffDays === 1) {
    // Yesterday: use translated label when provided, otherwise fall back to a date
    return yesterdayLabel ?? d.toLocaleDateString(locale, { day: '2-digit', month: 'short' })
  }
  if (diffDays < 7 && diffDays > 0) {
    // Last 7 days: show weekday
    return d.toLocaleDateString(locale, { weekday: 'short' })
  }
  // Older: show date
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short' })
}

interface RelativeTimeLabels {
  dayAgo?: (count: number) => string
  hourAgo?: (count: number) => string
  justNow?: string
  minuteAgo?: (count: number) => string
  now?: string
  yesterday?: string
}

export function formatRelativeTime(
  date: Date | string,
  labels?: RelativeTimeLabels,
  locale?: string
): string {
  const d = new Date(date)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) {
    return labels?.justNow ?? 'just now'
  }
  if (diffMinutes < 60) {
    return (
      labels?.minuteAgo?.(diffMinutes) ?? `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
    )
  }
  if (diffHours < 24) {
    return labels?.hourAgo?.(diffHours) ?? `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  }
  if (diffDays === 1) {
    return labels?.yesterday ?? 'yesterday'
  }
  if (diffDays < 7) {
    return labels?.dayAgo?.(diffDays) ?? `${diffDays} days ago`
  }
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
}

interface RelativeTimeShortLabels {
  dayAgo?: (count: number) => string
  hourAgo?: (count: number) => string
  minuteAgo?: (count: number) => string
  now?: string
}

export function formatRelativeTimeShort(
  date: Date | string,
  labels?: RelativeTimeShortLabels,
  locale?: string
): string {
  const d = new Date(date)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) {
    return labels?.now ?? 'now'
  }
  if (diffMinutes < 60) {
    return labels?.minuteAgo?.(diffMinutes) ?? `${diffMinutes}m ago`
  }
  if (diffHours < 24) {
    return labels?.hourAgo?.(diffHours) ?? `${diffHours}h ago`
  }
  if (diffDays < 7) {
    return labels?.dayAgo?.(diffDays) ?? `${diffDays}d ago`
  }
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
}

interface PendingUntilShortLabels {
  inDays?: (count: number) => string
  inHours?: (count: number) => string
  inMinutes?: (count: number) => string
  tomorrow?: string
}

/**
 * Format a future date (e.g. pending_until) for short display in badges.
 * Returns: "in Xm", "in Xh", "Tomorrow", "in Xd", or formatted date.
 */
export function formatPendingUntilShort(
  isoDate: string | null | undefined,
  labels?: PendingUntilShortLabels,
  locale?: string
): string {
  if (!isoDate) {
    return ''
  }
  const d = new Date(isoDate)
  const now = new Date()
  const diffMs = d.getTime() - now.getTime()
  if (diffMs <= 0) {
    return ''
  }
  const diffMinutes = Math.floor(diffMs / (60 * 1000))
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const isTomorrow =
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate()

  if (diffMinutes < 60) {
    return labels?.inMinutes?.(diffMinutes) ?? `in ${diffMinutes}m`
  }
  if (diffHours < 24) {
    return labels?.inHours?.(diffHours) ?? `in ${diffHours}h`
  }
  if (isTomorrow) {
    return labels?.tomorrow ?? 'Tomorrow'
  }
  if (diffDays < 7) {
    return labels?.inDays?.(diffDays) ?? `in ${diffDays}d`
  }
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
}

/**
 * Parse a date-only string (YYYY-MM-DD) as a local date, avoiding UTC timezone shifts.
 * Use this instead of `new Date(dateStr)` for DATE columns (scheduled_date, etc.)
 * which have no time component. `new Date("2025-02-19")` parses as UTC midnight, which
 * can shift to the previous day in western timezones (e.g. EST shows Feb 18).
 */
export function parseDateOnly(dateStr: string): Date {
  const [year, month, day] = dateStr.split('T')[0].split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Return a Date whose local Y/M/D match the current calendar date in the given timezone.
 *
 * The returned Date is "floating": its UTC instant is whatever browser-local midnight on
 * the workspace's calendar day happens to be. This lets date-fns helpers
 * (startOfWeek, addDays, format(_, 'yyyy-MM-dd'), isSameDay) produce the workspace date
 * even when the browser is in a different timezone.
 *
 * Without this, `new Date()` gives the browser's "now" — so an operator in America/Los_Angeles
 * editing a schedule at 11pm PT sees Sunday's grid when the workspace in Asia/Tokyo is already
 * on Monday. Falls back to browser local when timezone is null/undefined.
 */
export function workspaceToday(timezone: string | null | undefined): Date {
  if (!timezone) {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const year = Number(parts.find((p) => p.type === 'year')?.value)
  const month = Number(parts.find((p) => p.type === 'month')?.value)
  const day = Number(parts.find((p) => p.type === 'day')?.value)
  if (!(Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day))) {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }
  return new Date(year, month - 1, day)
}

/**
 * Format a Date object as 'YYYY-MM-DD' using LOCAL time.
 * Use this instead of `date.toISOString().split('T')[0]` which converts to UTC
 * and can shift the date backwards for users in UTC+ timezones.
 */
export function formatDateOnly(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export interface RelativeDateLabels {
  daysAgo: (count: number) => string
  inDays: (count: number) => string
  today: string
  tomorrow: string
  yesterday: string
}

/**
 * Format a date relative to today for short display.
 * Handles both DATE-only strings ('YYYY-MM-DD') and full ISO timestamps.
 * For timestamps, the local date is derived from the browser timezone.
 */
export function formatScheduledDateShort(
  dateStr: string | null | undefined,
  labels?: RelativeDateLabels,
  intlLocale?: string
): string {
  if (!dateStr) {
    return ''
  }
  // For full ISO timestamps (contains 'T' and timezone info), derive local date from Date object
  // to avoid UTC-date mismatch. For DATE-only strings, use parseDateOnly.
  const isTimestamp = dateStr.includes('T') && dateStr.length > 10
  const d = isTimestamp
    ? (() => {
        const dt = new Date(dateStr)
        return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
      })()
    : parseDateOnly(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const diffDays = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return labels?.today ?? 'Today'
  }
  if (diffDays === 1) {
    return labels?.tomorrow ?? 'Tmrw'
  }
  if (diffDays === -1) {
    return labels?.yesterday ?? 'Ystrdy'
  }
  if (diffDays > 1 && diffDays <= 14) {
    return labels?.inDays(diffDays) ?? `in ${diffDays}d`
  }
  if (diffDays < -1 && diffDays >= -14) {
    return labels?.daysAgo(Math.abs(diffDays)) ?? `${Math.abs(diffDays)}d ago`
  }
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: '2-digit' }
  return d.toLocaleDateString(intlLocale, opts)
}

export function formatDateRange(
  checkin: string | null,
  checkout: string | null,
  locale?: string,
  separator = ' \u2013 '
): string {
  if (!(checkin && checkout)) {
    return ''
  }
  const start = parseDateOnly(checkin)
  const end = parseDateOnly(checkout)
  const formatOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  return `${start.toLocaleDateString(locale, formatOpts)}${separator}${end.toLocaleDateString(locale, formatOpts)}`
}

export function getReservationStatusInfo(
  checkinDate: string | null,
  checkoutDate: string | null,
  status: string | null
): { variant: 'inquiry' | 'future' | 'checkedIn' | 'past' | 'cancelled' } {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  if (status?.toLowerCase() === 'cancelled') {
    return { variant: 'cancelled' }
  }

  if (status?.toLowerCase() === 'inquiry' || status?.toLowerCase() === 'pending') {
    return { variant: 'inquiry' }
  }

  if (!(checkinDate && checkoutDate)) {
    return { variant: 'inquiry' }
  }

  const checkin = parseDateOnly(checkinDate)
  const checkout = parseDateOnly(checkoutDate)
  const checkinDay = new Date(checkin.getFullYear(), checkin.getMonth(), checkin.getDate())
  const checkoutDay = new Date(checkout.getFullYear(), checkout.getMonth(), checkout.getDate())

  if (today < checkinDay) {
    return { variant: 'future' }
  }
  if (today >= checkinDay && today <= checkoutDay) {
    return { variant: 'checkedIn' }
  }
  return { variant: 'past' }
}

export function formatCurrency(amount: number, currency = 'EUR', locale?: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

/** Safely format an ISO date string; returns fallback on null/invalid input. */
export function safeFormat(dateStr: string | null | undefined, fmt: string, fallback = ''): string {
  if (!dateStr) {
    return fallback
  }
  try {
    const d = parseISO(dateStr)
    return isValid(d) ? format(d, fmt) : fallback
  } catch {
    return fallback
  }
}

/**
 * Normalize a domain string: strip protocol, www, path, port.
 * Used by credential CRUD routes and the browser profiles UI.
 */
export function normalizeDomain(raw: string): string {
  let d = raw.trim().toLowerCase()
  d = d.replace(DOMAIN_PROTOCOL_PATTERN, '')
  d = d.replace(DOMAIN_WWW_PATTERN, '')
  const [domainWithoutPath = ''] = d.split('/')
  const [domainWithoutPort = ''] = domainWithoutPath.split(':')
  return domainWithoutPort
}
